import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Modal, Form, Select, InputNumber, Input, DatePicker, message, Row, Col, Statistic, Drawer, Space, Rate, Progress, Empty } from 'antd';
import { PlusOutlined, UserOutlined, MessageOutlined, StarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiService from 'services/api/apiService';

const { TextArea } = Input;

interface BuddyMentorshipProps { employees: any[]; }

const BuddyMentorship: React.FC<BuddyMentorshipProps> = ({ employees }) => {
  const [assignments, setAssignments]       = useState<any[]>([]);
  const [summary, setSummary]               = useState<any>(null);
  const [eligibleBuddies, setEligibleBuddies] = useState<any[]>([]);
  const [loading, setLoading]               = useState(false);
  const [assignModal, setAssignModal]       = useState(false);
  const [feedbackDrawer, setFeedbackDrawer] = useState(false);
  const [feedbackModal, setFeedbackModal]   = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<any>(null);
  const [feedbackList, setFeedbackList]     = useState<any[]>([]);
  const [assignForm]   = Form.useForm();
  const [feedbackForm] = Form.useForm();

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [ar, sr, br] = await Promise.all([
        apiService.getBuddyAssignments(),
        apiService.getBuddySummary(),
        apiService.getEligibleBuddies(),
      ]);
      if (ar.success) setAssignments(ar.data || []);
      if (sr.success) setSummary(sr.data);
      if (br.success) setEligibleBuddies(br.data || []);
    } finally { setLoading(false); }
  };

  const openFeedback = async (assignment: any) => {
    setSelectedAssignment(assignment);
    setFeedbackDrawer(true);
    const r = await apiService.getBuddyFeedback(assignment.id);
    if (r.success) setFeedbackList(r.data || []);
  };

  const handleAssign = async (values: any) => {
    const newEmp = employees.find(e => e.id === values.new_employee_id);
    const buddy  = eligibleBuddies.find(e => e.id === values.buddy_employee_id);
    if (!newEmp || !buddy) { message.error('Employee not found'); return; }
    const r = await apiService.createBuddyAssignment({
      ...values,
      new_employee_name:   `${newEmp.first_name} ${newEmp.last_name}`,
      buddy_employee_name: `${buddy.first_name} ${buddy.last_name}`,
      assignment_date: values.assignment_date?.format('YYYY-MM-DD') || dayjs().format('YYYY-MM-DD'),
    });
    if (r.success) { message.success('Buddy assigned successfully'); setAssignModal(false); assignForm.resetFields(); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const handleFeedback = async (values: any) => {
    const r = await apiService.submitBuddyFeedback({
      ...values,
      onboarding_id:   selectedAssignment.id,
      buddy_id:        selectedAssignment.buddy_employee_id,
      new_employee_id: selectedAssignment.new_employee_id,
      feedback_date:   dayjs().format('YYYY-MM-DD'),
    });
    if (r.success) {
      message.success('Feedback submitted');
      setFeedbackModal(false);
      feedbackForm.resetFields();
      const rr = await apiService.getBuddyFeedback(selectedAssignment.id);
      if (rr.success) setFeedbackList(rr.data || []);
    } else message.error(r.error || 'Failed');
  };

  const statusColor: Record<string, string> = { active: 'processing', completed: 'success', terminated: 'error' };

  const columns = [
    { title: 'New Employee', dataIndex: 'new_employee_full', render: (v: string) => v || 'N/A', width: 160 },
    { title: 'Department', dataIndex: 'department', width: 120 },
    { title: 'Buddy', dataIndex: 'buddy_full', render: (v: string) => v || 'N/A', width: 160 },
    { title: 'Buddy Dept.', dataIndex: 'buddy_department', width: 120 },
    { title: 'Duration', dataIndex: 'duration_weeks', render: (v: number) => `${v} weeks`, width: 90 },
    { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={statusColor[v]||'default'}>{v?.toUpperCase()}</Tag>, width: 100 },
    { title: 'Feedback', dataIndex: 'feedback_count', render: (v: number) => `${v} sessions`, width: 100 },
    { title: 'Rating', dataIndex: 'feedback_rating', render: (v: number) => v ? <Rate disabled defaultValue={v} count={5} style={{fontSize:14}} /> : '-', width: 120 },
    { title: 'Actions', render: (_: any, r: any) => (
      <Space>
        <Button size="small" icon={<MessageOutlined />} onClick={() => openFeedback(r)}>Feedback</Button>
        {r.status === 'active' && (
          <Button size="small" onClick={() => apiService.updateBuddyAssignment(r.id, { status: 'completed' }).then(() => { message.success('Marked complete'); fetchAll(); })}>
            Complete
          </Button>
        )}
      </Space>
    ), width: 180 },
  ];

  return (
    <div>
      {/* Summary stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}><Card><Statistic title="Total Assignments" value={summary?.total_assignments || 0} /></Card></Col>
        <Col span={6}><Card><Statistic title="Active" value={summary?.active || 0} valueStyle={{ color: '#1890ff' }} /></Card></Col>
        <Col span={6}><Card><Statistic title="Completed" value={summary?.completed || 0} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col span={6}><Card><Statistic title="Avg Rating" value={summary?.avg_rating || 0} suffix="/5" precision={1} /></Card></Col>
      </Row>

      <Card
        title="Buddy Mentorship Assignments"
        loading={loading}
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setAssignModal(true)}>Assign Buddy</Button>}
      >
        <Table columns={columns} dataSource={assignments} rowKey="id" pagination={{ pageSize: 10 }} scroll={{ x: 1100 }} />
      </Card>

      {/* Assign Buddy Modal */}
      <Modal title="Assign Buddy Mentor" open={assignModal} onCancel={() => { setAssignModal(false); assignForm.resetFields(); }} footer={null} width={600}>
        <Form form={assignForm} layout="vertical" onFinish={handleAssign}>
          <Form.Item name="new_employee_id" label="New Employee" rules={[{required:true}]}>
            <Select placeholder="Select new employee" showSearch optionFilterProp="children">
              {employees.map(e => <Select.Option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.department}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="buddy_employee_id" label="Buddy / Mentor" rules={[{required:true}]} extra="Only employees with fewer than 3 active assignments are shown">
            <Select placeholder="Select buddy" showSearch optionFilterProp="children">
              {eligibleBuddies.map(e => <Select.Option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.department} ({e.active_assignments} active)</Select.Option>)}
            </Select>
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="assignment_date" label="Start Date" rules={[{required:true}]}><DatePicker style={{width:'100%'}} /></Form.Item></Col>
            <Col span={12}><Form.Item name="duration_weeks" label="Duration (weeks)" initialValue={4}><InputNumber min={1} max={52} style={{width:'100%'}} /></Form.Item></Col>
          </Row>
          <Form.Item name="buddy_notes" label="Notes / Goals"><TextArea rows={3} placeholder="Goals for this mentorship..." /></Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">Assign Buddy</Button>
            <Button onClick={() => { setAssignModal(false); assignForm.resetFields(); }}>Cancel</Button>
          </Space>
        </Form>
      </Modal>

      {/* Feedback Drawer */}
      <Drawer
        title={`Buddy Feedback — ${selectedAssignment?.new_employee_full || ''}`}
        placement="right" width={600}
        open={feedbackDrawer}
        onClose={() => setFeedbackDrawer(false)}
        extra={<Button type="primary" size="small" onClick={() => setFeedbackModal(true)}>Add Weekly Feedback</Button>}
      >
        {selectedAssignment && (
          <div style={{ marginBottom: 16 }}>
            <p><strong>New Employee:</strong> {selectedAssignment.new_employee_full}</p>
            <p><strong>Buddy:</strong> {selectedAssignment.buddy_full}</p>
            <p><strong>Department:</strong> {selectedAssignment.department}</p>
            <Progress
              percent={Math.min(100, ((selectedAssignment.feedback_count || 0) / (selectedAssignment.duration_weeks || 4)) * 100)}
              format={() => `Week ${selectedAssignment.latest_week || 0} of ${selectedAssignment.duration_weeks}`}
            />
          </div>
        )}
        {feedbackList.length === 0 ? <Empty description="No feedback submitted yet" /> : (
          feedbackList.map((fb: any) => (
            <Card key={fb.id} size="small" style={{ marginBottom: 12 }}
              title={<span>Week {fb.week_number} — {fb.feedback_date}</span>}
              extra={<><Rate disabled defaultValue={fb.progress_rating} count={5} style={{fontSize:12}} /></>}
            >
              <Row gutter={8}>
                <Col span={12}><Statistic title="Progress" value={fb.progress_rating} suffix="/5" /></Col>
                <Col span={12}><Statistic title="Engagement" value={fb.engagement_rating} suffix="/5" /></Col>
              </Row>
              {fb.feedback_notes && <p style={{ marginTop: 8 }}><strong>Notes:</strong> {fb.feedback_notes}</p>}
              {fb.challenges && <p><strong>Challenges:</strong> {fb.challenges}</p>}
              {fb.next_week_goals && <p><strong>Next Week Goals:</strong> {fb.next_week_goals}</p>}
            </Card>
          ))
        )}
      </Drawer>

      {/* Add Feedback Modal */}
      <Modal title="Submit Weekly Feedback" open={feedbackModal} onCancel={() => { setFeedbackModal(false); feedbackForm.resetFields(); }} footer={null} width={600}>
        <Form form={feedbackForm} layout="vertical" onFinish={handleFeedback}>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="week_number" label="Week Number" rules={[{required:true}]}><InputNumber min={1} max={52} style={{width:'100%'}} /></Form.Item></Col>
            <Col span={12}><Form.Item name="progress_rating" label="Progress Rating (1-5)" rules={[{required:true}]}><InputNumber min={1} max={5} style={{width:'100%'}} /></Form.Item></Col>
          </Row>
          <Form.Item name="engagement_rating" label="Engagement Rating (1-5)" rules={[{required:true}]}><InputNumber min={1} max={5} style={{width:'100%'}} /></Form.Item>
          <Form.Item name="feedback_notes" label="Feedback Notes" rules={[{required:true}]}><TextArea rows={3} /></Form.Item>
          <Form.Item name="challenges" label="Challenges Observed"><TextArea rows={2} /></Form.Item>
          <Form.Item name="recommendations" label="Recommendations"><TextArea rows={2} /></Form.Item>
          <Form.Item name="next_week_goals" label="Next Week Goals"><TextArea rows={2} /></Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">Submit Feedback</Button>
            <Button onClick={() => { setFeedbackModal(false); feedbackForm.resetFields(); }}>Cancel</Button>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default BuddyMentorship;
